// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { sharedDateTimeOptions } from "@tps/XDL/constants";
import { parseBoolean } from "@tps/XDL/Utilities";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
    };
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    // xng-changes:
    downloadClickable?: ClickableProps;
    downloadButton?: ButtonPresentationProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<Props> = ({ fields, language }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!invoice) {
        return null;
    }

    const printLabel = translate("Print");
    const downloadLabel = siteMessage("Custom_Download_Invoice");
    const invoiceExist = parseBoolean(invoice?.properties?.invoiceExist);
    const standardFormattedDate = getLocalizedDateTime({
        dateTime: new Date(invoice.invoiceDate),
        language,
        options: {
            ...sharedDateTimeOptions,
        },
    });
    // xng-changes: Convert the date format from MM/DD/YYYY to MM-DD-YYYY
    const formattedInvoiceDate = standardFormattedDate.replace(/\//g, "-");
    const downloadUrl = `/api/tps/files/invoice/Generate/${invoice.invoiceNumber}/${formattedInvoiceDate}`;

    const downloadInvoice = () => {
        window.open(downloadUrl, "_blank");
    };

    // xng-changes: TPS-982 Add link/button to download Invoice
    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{`${translate("Invoice")} ${invoice.invoiceNumber}`}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    {invoiceExist && (
                        <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                            {/* <ShareEntityButton
                            entityId={invoice.invoiceNumber}
                            variant="clickable"
                            entityName="Invoice"
                            extendedStyles={styles.shareEntityButtonStyles}
                            generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                        />
                        <Clickable
                            {...styles.printClickable}
                            onClick={openPrintDialog}
                            data-test-selector="invoiceDetails_print"
                        >
                            {printLabel}
                        </Clickable> */}

                            <Clickable
                                href={downloadUrl}
                                {...styles.downloadClickable}
                                data-test-selector="invoiceDetails_download"
                                target="_blank"
                            >
                                {downloadLabel}
                            </Clickable>
                        </OverflowMenu>
                    )}
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    {/* <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    />
                    <Button {...styles.printButton} onClick={openPrintDialog} data-test-selector="invoiceDetails_print">
                        {printLabel}
                    </Button> */}
                    {invoiceExist && (
                        <Button
                            {...styles.downloadButton}
                            onClick={downloadInvoice}
                            data-test-selector="invoiceDetails_download"
                        >
                            {downloadLabel}
                        </Button>
                    )}
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InvoiceDetailHeader),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
